import { createElement } from "./dom_utils.js";
import { createTranscriptPanel } from "./transcript.js";
import { createEndDialog } from "./end_dialog.js";
import { initLiveKit } from "../livekit.js";
import {
  resolveMediaUrl,
  pauseMeeting,
  resumeMeeting,
  endMeeting,
  signalVideoEnded,
} from "../api.js";
import {
  MicIcon,
  MicOffIcon,
  PhoneEndIcon,
  PauseIcon,
  PlayIcon,
  FullscreenIcon,
  FullscreenExitIcon,
  SunIcon,
  MoonIcon,
} from "../icons.js";

/**
 * Conversation view component.
 *
 * @param {Object} opts
 * @param {Object} opts.employee    - Employee data
 * @param {string} opts.sessionId
 * @param {string} opts.meetingId
 * @param {string} opts.livekitUrl
 * @param {string} opts.token
 * @param {boolean} [opts.avatarEnabled] - Whether Bey avatar is active for this meeting
 * @param {string|null} [opts.avatarId]  - Resolved Bey avatar id
 * @param {Array}  opts.contentCards
 * @param {AudioContext} opts.audioCtx
 * @param {Function} opts.onEnd     - Called when the call is ended
 * @returns {{ el: HTMLElement, destroy: Function }}
 */
export function createConversation({
  employee,
  sessionId,
  meetingId,
  livekitUrl,
  token,
  avatarEnabled = false,
  avatarId = null,
  contentCards,
  audioCtx,
  onEnd,
  onThemeToggle,
  getTheme,
  initialMessage,
}) {
  let isFullscreen = false;
  let showEndDialog = false;
  let endDialogInstance = null;
  let audioEl = null;
  let thinkingTimer = null;
  let showThinking = false;
  let videoEl = null;
  let avatarVideoEl = null;
  let prevVideoTrack = null;
  let _videoSignalNeeded = false;

  // Container
  const container = createElement("div", { className: "sm-conversation" });

  // === Header ===
  const header = createElement("div", { className: "sm-conv-header" });

  // Left: employee name
  const headerLeft = createElement("div", { className: "sm-conv-header-left" });
  headerLeft.appendChild(
    createElement("span", { className: "sm-conv-header-title" }, employee.name),
  );

  // Center: CTA button (inline in header)
  const headerCenter = createElement("div", {
    className: "sm-conv-header-center",
  });
  const presWin = employee.cta_config?.presentation_window;
  if (presWin?.enabled && presWin?.call_to_action_text) {
    const ctaBtn = createElement(
      "button",
      { className: "sm-cta-btn" },
      presWin.call_to_action_text,
    );
    ctaBtn.addEventListener("click", () => {
      let url = presWin.redirect_url || "";
      if (url && !url.match(/^https?:\/\//i)) url = "https://" + url;
      if (url) window.open(url, "_blank", "noopener");
    });
    headerCenter.appendChild(ctaBtn);
  }

  // Right: theme toggle + fullscreen
  const headerRight = createElement("div", {
    className: "sm-conv-header-right",
  });

  let convThemeBtn = null;
  if (onThemeToggle && getTheme) {
    convThemeBtn = createElement("button", { className: "sm-conv-header-btn" });
    convThemeBtn.setAttribute("aria-label", "Toggle theme");
    const updateConvThemeIcon = () => {
      convThemeBtn.innerHTML = getTheme() === "light" ? MoonIcon : SunIcon;
    };
    updateConvThemeIcon();
    convThemeBtn.addEventListener("click", () => {
      onThemeToggle();
      updateConvThemeIcon();
    });
    headerRight.appendChild(convThemeBtn);
  }

  const fullscreenBtn = createElement("button", {
    className: "sm-conv-header-btn",
  });
  fullscreenBtn.setAttribute("aria-label", "Fullscreen");
  fullscreenBtn.innerHTML = FullscreenIcon;
  headerRight.appendChild(fullscreenBtn);

  header.appendChild(headerLeft);
  header.appendChild(headerCenter);
  header.appendChild(headerRight);
  container.appendChild(header);

  // === Body (2-column) ===
  const body = createElement("div", { className: "sm-conv-body" });

  // Transcript panel
  const transcript = createTranscriptPanel({ employeeName: employee.name });
  body.appendChild(transcript.el);

  // Slide area
  const slideArea = createElement("div", { className: "sm-conv-slide" });

  // Video element (single reused instance, hidden by default)
  videoEl = createElement("video", { className: "sm-conv-slide-video" });
  videoEl.muted = true;
  videoEl.playsInline = true;
  videoEl.controls = false;
  videoEl.loop = false;
  videoEl.preload = "auto";
  videoEl.style.display = "none";
  videoEl.addEventListener("ended", () => {
    if (!videoEl) return;
    // Keep showing the last frame — stopSlideVideo() will hide it when the next slide arrives.
    // This prevents a blank slide area between video end and the next slide publish.
    if (_videoSignalNeeded) {
      _videoSignalNeeded = false;
      signalVideoEnded(meetingId, sessionId).catch((err) =>
        console.warn("[Video] signalVideoEnded failed:", err),
      );
    }
  });
  videoEl.addEventListener("error", () => {
    if (!videoEl) return;
    console.warn("[Video] Failed to load video");
    videoEl.style.display = "none";
    // Signal video ended so the backend doesn't hang waiting
    if (_videoSignalNeeded) {
      _videoSignalNeeded = false;
      signalVideoEnded(meetingId, sessionId).catch((err) =>
        console.warn("[Video] signalVideoEnded (error) failed:", err),
      );
    }
  });
  slideArea.appendChild(videoEl);

  // Avatar video element (Bey / Hedra talking-head), rendered as a
  // picture-in-picture in the bottom-right of the slide area so slides and the
  // avatar are visible simultaneously (Google Meet / Zoom style). Hidden by
  // default; shown only when avatarEnabled && a remote video track has been
  // subscribed.
  slideArea.style.position = slideArea.style.position || "relative";
  avatarVideoEl = createElement("video", {
    className: "sm-conv-avatar-video",
  });
  avatarVideoEl.muted = true; // audio plays via the separate agentAudioTrack path
  avatarVideoEl.playsInline = true;
  avatarVideoEl.autoplay = true;
  avatarVideoEl.controls = false;
  avatarVideoEl.style.display = "none";
  avatarVideoEl.style.position = "absolute";
  avatarVideoEl.style.bottom = "12px";
  avatarVideoEl.style.right = "12px";
  avatarVideoEl.style.width = "48%";
  avatarVideoEl.style.maxWidth = "420px";
  avatarVideoEl.style.minWidth = "280px";
  avatarVideoEl.style.aspectRatio = "16 / 9";
  avatarVideoEl.style.objectFit = "cover";
  avatarVideoEl.style.borderRadius = "12px";
  avatarVideoEl.style.boxShadow = "0 4px 16px rgba(0, 0, 0, 0.35)";
  avatarVideoEl.style.border = "2px solid rgba(255, 255, 255, 0.25)";
  avatarVideoEl.style.backgroundColor = "#000";
  avatarVideoEl.style.zIndex = "10";
  avatarVideoEl.style.pointerEvents = "none";
  slideArea.appendChild(avatarVideoEl);

  // Thinking overlay (hidden initially)
  const thinkingOverlay = createElement("div", {
    className: "sm-thinking-overlay",
  });
  thinkingOverlay.style.display = "none";
  thinkingOverlay.appendChild(
    createElement("span", { className: "sm-thinking-pill" }, "Thinking..."),
  );
  slideArea.appendChild(thinkingOverlay);

  // Greeting (default slide content)
  const greetingArea = createElement("div", {
    className: "sm-conv-greeting-area",
  });
  greetingArea.appendChild(
    createElement(
      "div",
      { className: "sm-conv-greeting-avatar" },
      employee.name.charAt(0),
    ),
  );
  greetingArea.appendChild(
    createElement("p", { className: "sm-conv-greeting-name" }, employee.name),
  );
  greetingArea.appendChild(
    createElement(
      "p",
      { className: "sm-conv-greeting-hint" },
      "Slides will appear here during the conversation",
    ),
  );
  slideArea.appendChild(greetingArea);

  body.appendChild(slideArea);

  function playSlideVideo(url) {
    if (!videoEl) return;
    // Reset media only — do NOT signal; caller (updateSlide) already called stopSlideVideo
    if (!videoEl.paused) videoEl.pause();
    _videoSignalNeeded = true;
    const resolvedUrl = resolveMediaUrl(url);
    videoEl.src = resolvedUrl;
    videoEl.style.display = "";
    // Wait until enough data is buffered before attempting playback
    const tryPlay = () => {
      if (!videoEl) return;
      const p = videoEl.play();
      if (p !== undefined) {
        p.catch((err) => {
          console.warn("[Video] Autoplay blocked:", err);
          // Last resort: show controls so user can manually tap play
          if (videoEl) videoEl.controls = true;
        });
      }
    };
    if (videoEl.readyState >= 3) {
      // HAVE_FUTURE_DATA or higher — already buffered enough
      tryPlay();
    } else {
      videoEl.addEventListener("canplay", tryPlay, { once: true });
      videoEl.load();
    }
  }

  function stopSlideVideo() {
    if (!videoEl) return;
    if (!videoEl.paused) videoEl.pause();
    videoEl.removeAttribute("src");
    videoEl.load();
    videoEl.controls = false;
    videoEl.style.display = "none";
    if (_videoSignalNeeded) {
      _videoSignalNeeded = false;
      signalVideoEnded(meetingId, sessionId).catch((err) =>
        console.warn("[Video] signalVideoEnded (stop) failed:", err),
      );
    }
  }

  container.appendChild(body);

  // === Footer ===
  const footer = createElement("div", { className: "sm-conv-footer" });

  // Left: powered by
  const footerLeft = createElement("div", {
    className: "sm-conv-footer-section sm-conv-footer-left",
  });
  const powered = createElement("div", { className: "sm-powered" });
  powered.appendChild(
    createElement("span", { className: "sm-powered-label" }, "Powered by"),
  );
  powered.appendChild(
    createElement(
      "span",
      { className: "sm-powered-brand" },
      "&lt;/&gt; SuperAGI",
    ),
  );
  footerLeft.appendChild(powered);

  // Center: controls
  const footerCenter = createElement("div", {
    className: "sm-conv-footer-section sm-conv-footer-center",
  });
  const micBtn = createElement("button", { className: "sm-conv-ctrl" });
  micBtn.setAttribute("aria-label", "Mute microphone");
  micBtn.innerHTML = MicIcon;

  const pauseBtn = createElement("button", { className: "sm-conv-ctrl" });
  pauseBtn.setAttribute("aria-label", "Pause");
  pauseBtn.innerHTML = PauseIcon;

  footerCenter.appendChild(micBtn);
  footerCenter.appendChild(pauseBtn);

  // Right: end call
  const footerRight = createElement("div", {
    className: "sm-conv-footer-section sm-conv-footer-right",
  });
  const endBtn = createElement("button", {
    className: "sm-conv-ctrl sm-conv-ctrl--end",
  });
  endBtn.setAttribute("aria-label", "End call");
  endBtn.innerHTML = PhoneEndIcon;
  footerRight.appendChild(endBtn);

  footer.appendChild(footerLeft);
  footer.appendChild(footerCenter);
  footer.appendChild(footerRight);
  container.appendChild(footer);

  // === Transcript send handler ===
  transcript.onSend((text) => {
    livekit.sendTextMessage(text);
    // Clear suggestions on any send
    lastSuggestionsKey = "";
    transcript.updateSuggestions([]);
  });

  // === FAQ suggestion chips (shown after first exchange) ===
  let faqChipsShown = false;
  const faqCfg = employee.faq_config;
  let faqQuestions = [];
  if (faqCfg?.enabled && faqCfg?.content) {
    faqQuestions = faqCfg.content
      .split("\n")
      .map((q) => q.trim())
      .filter((q) => q.length > 0);
  }

  // === LiveKit ===
  let prevAudioTrack = null;
  let lastSuggestionsKey = "";

  const livekit = initLiveKit({
    url: livekitUrl,
    token,
    audioCtx,
    initialMessage,
    onStateChange(state) {
      // Update transcript
      transcript.update(state.transcriptMessages);

      // Update mic button
      if (state.isMicOn) {
        micBtn.className = "sm-conv-ctrl";
        micBtn.innerHTML = MicIcon;
        micBtn.setAttribute("aria-label", "Mute microphone");
      } else {
        micBtn.className = "sm-conv-ctrl sm-conv-ctrl--muted";
        micBtn.innerHTML = MicOffIcon;
        micBtn.setAttribute("aria-label", "Unmute microphone");
      }

      // Show/hide text input based on mute state
      transcript.setInputVisible(!state.isMicOn);

      // Show FAQ chips after first exchange (2+ transcript messages)
      if (
        !faqChipsShown &&
        faqQuestions.length > 0 &&
        (state.transcriptMessages || []).length >= 2
      ) {
        faqChipsShown = true;
        transcript.updateSuggestions(faqQuestions);
      }

      // Show suggestions persistently until the user sends a new message
      const newSuggestionsKey = (state.suggestions || []).join("|");
      if (newSuggestionsKey !== lastSuggestionsKey) {
        lastSuggestionsKey = newSuggestionsKey;
        transcript.updateSuggestions(state.suggestions || []);
      }

      // Update pause button
      if (state.isPaused) {
        pauseBtn.className = "sm-conv-ctrl sm-conv-ctrl--paused";
        pauseBtn.innerHTML = PlayIcon;
        pauseBtn.setAttribute("aria-label", "Resume");
        micBtn.style.opacity = "0.4";
        micBtn.style.pointerEvents = "none";
      } else {
        pauseBtn.className = "sm-conv-ctrl";
        pauseBtn.innerHTML = PauseIcon;
        pauseBtn.setAttribute("aria-label", "Pause");
        micBtn.style.opacity = "";
        micBtn.style.pointerEvents = "";
      }

      // Update thinking indicator with 3.5s delay
      if (state.agentStatus === "thinking") {
        if (!thinkingTimer) {
          thinkingTimer = setTimeout(() => {
            showThinking = true;
            thinkingOverlay.style.display = "";
          }, 3500);
        }
      } else {
        if (thinkingTimer) {
          clearTimeout(thinkingTimer);
          thinkingTimer = null;
        }
        showThinking = false;
        thinkingOverlay.style.display = "none";
      }

      // Update slide content — only when slide state actually changes
      if (
        state.currentSlide !== _prevSlide ||
        state.currentSlideMediaType !== _prevSlideMediaType ||
        state.currentSlideMediaUrl !== _prevSlideMediaUrl
      ) {
        updateSlide(
          state.currentSlide,
          state.currentSlideMediaType,
          state.currentSlideMediaUrl,
        );
        _prevSlide = state.currentSlide;
        _prevSlideMediaType = state.currentSlideMediaType;
        _prevSlideMediaUrl = state.currentSlideMediaUrl;
      }

      // Attach/detach agent audio track
      if (state.agentAudioTrack !== prevAudioTrack) {
        if (prevAudioTrack && audioEl) {
          prevAudioTrack.detach(audioEl);
          audioEl.remove();
          audioEl = null;
        }
        if (state.agentAudioTrack) {
          audioEl = state.agentAudioTrack.attach();
          document
            .getElementById("digital-employee-widget")
            ?.appendChild(audioEl);
        }
        prevAudioTrack = state.agentAudioTrack;
      }

      // Attach/detach agent video track (Bey / Hedra avatar).
      // Gated on avatarEnabled so a backend flag-off meeting never renders a
      // stray <video>, and gated on the track existence so a Bey start failure
      // gracefully falls back to the existing slide UI.
      if (state.agentVideoTrack !== prevVideoTrack) {
        if (prevVideoTrack && avatarVideoEl) {
          try {
            prevVideoTrack.detach(avatarVideoEl);
          } catch (err) {
            console.warn("[Avatar] detach failed:", err);
          }
        }
        if (avatarEnabled && state.agentVideoTrack && avatarVideoEl) {
          try {
            state.agentVideoTrack.attach(avatarVideoEl);
            avatarVideoEl.style.display = "block";
          } catch (err) {
            console.warn("[Avatar] attach failed:", err);
            avatarVideoEl.style.display = "none";
          }
        } else if (avatarVideoEl) {
          avatarVideoEl.style.display = "none";
        }
        prevVideoTrack = state.agentVideoTrack;
      }
    },
  });

  // === Slide helpers ===
  let currentSlideContent = null; // The element currently showing slide content
  let _prevSlide = undefined;
  let _prevSlideMediaType = undefined;
  let _prevSlideMediaUrl = undefined;

  function getActiveCard(slidePosition) {
    if (!contentCards || contentCards.length === 0) return null;
    return contentCards.find((c) => c.position === slidePosition) ?? null;
  }

  function updateSlide(slidePosition, slideMediaType, slideMediaUrl) {
    const card = getActiveCard(slidePosition);
    stopSlideVideo();

    if (currentSlideContent) {
      currentSlideContent.remove();
      currentSlideContent = null;
    }
    if (greetingArea.parentNode === slideArea) greetingArea.remove();

    // Video slide — URL comes from the data channel message
    if (slideMediaType && slideMediaType.startsWith("video") && slideMediaUrl) {
      playSlideVideo(slideMediaUrl);
      return;
    }

    // Image or text slide — same as existing logic
    if (card) {
      if (
        card.media_url &&
        card.media_type &&
        card.media_type.startsWith("video")
      ) {
        // Video content card — use the reusable video element via playSlideVideo
        playSlideVideo(card.media_url);
        return;
      } else if (card.media_url) {
        const img = createElement("img", {
          className: "sm-conv-slide-img",
        });
        img.src = resolveMediaUrl(card.media_url);
        img.alt = card.title || "";
        currentSlideContent = img;
      } else {
        const inner = createElement("div", {
          className: "sm-conv-slide-inner",
        });
        inner.appendChild(
          createElement(
            "p",
            { className: "sm-conv-slide-title" },
            card.title || "",
          ),
        );
        inner.appendChild(
          createElement(
            "p",
            { className: "sm-conv-slide-desc" },
            card.description || "",
          ),
        );
        currentSlideContent = inner;
      }
      slideArea.appendChild(currentSlideContent);
    } else {
      slideArea.appendChild(greetingArea);
      currentSlideContent = null;
    }
  }

  // === Event handlers ===

  micBtn.addEventListener("click", () => livekit.toggleMic());

  pauseBtn.addEventListener("click", async () => {
    if (!meetingId) return;
    const state = livekit.getState();
    if (state.isPaused) {
      await resumeMeeting(meetingId, sessionId).catch(() => {});
      livekit.resumeCall();
    } else {
      stopSlideVideo();
      await pauseMeeting(meetingId, sessionId).catch(() => {});
      livekit.pauseCall();
    }
  });

  endBtn.addEventListener("click", () => {
    openEndDialog();
  });

  function openEndDialog() {
    if (endDialogInstance) return;
    showEndDialog = true;
    endDialogInstance = createEndDialog({
      onConfirm: handleEndCall,
      onCancel: closeEndDialog,
      employee,
    });
    container.appendChild(endDialogInstance.el);
  }

  function closeEndDialog() {
    showEndDialog = false;
    if (endDialogInstance) {
      endDialogInstance.destroy();
      endDialogInstance = null;
    }
  }

  async function handleEndCall() {
    stopSlideVideo();
    closeEndDialog();
    if (meetingId) {
      try {
        await endMeeting(meetingId, sessionId);
      } catch (err) {
        console.warn("End meeting API failed:", err);
      }
    }
    livekit.disconnectRoom();

    // Redirect on end chat if configured
    const endWin = employee.cta_config?.end_chat_window;
    if (endWin?.end_chat_redirect_enabled && endWin?.end_chat_redirect_url) {
      let url = endWin.end_chat_redirect_url;
      if (!url.match(/^https?:\/\//i)) url = "https://" + url;
      try {
        window.top.location.href = url;
      } catch {
        window.location.href = url;
      }
      return;
    }

    onEnd();
  }

  // === Fullscreen ===

  fullscreenBtn.addEventListener("click", toggleFullscreen);

  function toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {
        isFullscreen = !isFullscreen;
        updateFullscreenUI();
      });
    } else {
      document.exitFullscreen?.().catch(() => {
        isFullscreen = false;
        updateFullscreenUI();
      });
    }
  }

  function onFullscreenChange() {
    isFullscreen = !!document.fullscreenElement;
    updateFullscreenUI();
  }

  function updateFullscreenUI() {
    if (isFullscreen) {
      container.classList.add("sm-conversation--fullscreen");
      fullscreenBtn.innerHTML = FullscreenExitIcon;
      fullscreenBtn.setAttribute("aria-label", "Exit fullscreen");
    } else {
      container.classList.remove("sm-conversation--fullscreen");
      fullscreenBtn.innerHTML = FullscreenIcon;
      fullscreenBtn.setAttribute("aria-label", "Fullscreen");
    }
  }

  document.addEventListener("fullscreenchange", onFullscreenChange);

  // === Destroy ===

  function destroy() {
    stopSlideVideo();
    videoEl = null;
    if (avatarVideoEl) {
      if (prevVideoTrack) {
        try {
          prevVideoTrack.detach(avatarVideoEl);
        } catch (err) {
          console.warn("[Avatar] detach on destroy failed:", err);
        }
        prevVideoTrack = null;
      }
      avatarVideoEl.remove();
      avatarVideoEl = null;
    }
    document.removeEventListener("fullscreenchange", onFullscreenChange);
    if (thinkingTimer) {
      clearTimeout(thinkingTimer);
      thinkingTimer = null;
    }
    if (audioEl) {
      audioEl.remove();
      audioEl = null;
    }
    closeEndDialog();
    transcript.destroy();
    livekit.disconnectRoom();
    container.remove();
  }

  return { el: container, destroy };
}
